<template>
    <div
        class="gw-footer"
        :style="[isRtlLanguageFunc(this.$globalConstants.host.language_code)]"
    >
        <div class="container">
            <section>
                <router-link
                    :to="{ path: fullPath('/') }"
                    class="footer-logo"
                    v-if="!$globalConstants.host.rtl_header"
                >
                    <img
                        v-show="logoUrl"
                        v-lazy="logoUrl"
                        alt="Footer logo"
                        width="180"
                        height="50"
                    />
                </router-link>

                <SocialMediaLinks />
            </section>
            <template v-if="footerReady">
                <section
                    v-for="(item, index) in reorganizedArray.slice(0, 3)"
                    :key="index"
                >
                    <div class="menu-item" v-if="item.main_links?.length">
                        <router-link
                            v-for="(item, index) in item.main_links"
                            :key="index"
                            :to="fullPath(item.route)"
                            class="menu-item__link"
                        >
                            {{ $t(item.name) }}
                        </router-link>
                    </div>

                    <div class="menu-item" v-else>
                        <router-link
                            :to="fullPath(item.route)"
                            class="menu-item__link"
                        >
                            {{ $t(item.name) }}
                        </router-link>

                        <ul
                            v-if="item.dropdown_items?.length"
                            class="menu-item__list"
                        >
                            <li
                                v-for="(
                                    dropdownItem, index
                                ) in item.dropdown_items"
                                :key="index"
                            >
                                <router-link
                                    v-if="showMenuItem(dropdownItem)"
                                    :to="fullPath(dropdownItem.route)"
                                >
                                    {{ $t(dropdownItem.name) }}
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </section>
            </template>
            <template v-else>
                <div class="footer-links-skeleton"></div>
            </template>
        </div>
        <div class="bottom">
            {{ $t('Copyright') }} © 2009-{{ fullYear }}
            {{
                !$globalConstants.host.rtl_header
                    ? $globalConstants.host.name
                    : ''
            }}
        </div>
    </div>
</template>

<script>
import { helper, contentDirectionMixin } from '@/ghost-mixins';
import SocialMediaLinks from '@molecules/SocialMediaLinks/SocialMediaLinks.vue';

export default {
    name: 'GhostWriterFooter',
    mixins: [helper, contentDirectionMixin],
    components: {
        SocialMediaLinks,
    },
    data() {
        return {
            fullYear: new Date().getFullYear(),
            defaultLinks: {
                name: this.$t('Info'),
                route: '/',
                dropdown_items: [
                    {
                        route: '/about-us',
                        name: this.$t('About Us'),
                    },
                    {
                        route: '/faq',
                        name: this.$t('FAQ'),
                    },
                    {
                        route: '/sitemap',
                        name: this.$t('Sitemap'),
                    },
                    {
                        route: '/imprint',
                        name: this.$globalConstants.host['imprint_text']
                            ? this.$globalConstants.host['imprint_text']
                            : this.$t('Imprint'),
                    },
                    {
                        route: '/privacy',
                        name: this.$t('Privacy'),
                    },
                    {
                        route: '/report-content',
                        name: this.$t('Report Illegal content'),
                    },
                ],
            },
        };
    },
    methods: {
        showMenuItem(item) {
            if (item.route !== '/report-content') return true;
            return this.showReportContent;
        },
    },
    computed: {
        footerReady() {
            const items = this.$globalConstants.host?.menu_items;
            return Array.isArray(items) && items.length > 0;
        },
        isAdsenseExternalProject() {
            return this.$globalConstants.host.project_id === 108;
        },
        filteredDefaultLinks() {
            if (!this.isAdsenseExternalProject) {
                return this.defaultLinks;
            }

            return {
                ...this.defaultLinks,
                dropdown_items: this.defaultLinks.dropdown_items.filter(
                    (item) => item.route !== '/report-content'
                ),
            };
        },
        logoUrl() {
            try {
                let src = this.$globalConstants.host.header_banner_dark;
                return this.resizeImage(src, 260, 60, 100);
            } catch (e) {
                return null;
            }
        },
        showReportContent() {
            return !this.isAdsenseExternalProject;
        },
        menuItems() {
            return this.$globalConstants.host.menu_items;
        },
        reorganizedArray() {
            let noDropdownItems = { main_links: [] };
            const hasDropdownItems = [];
            this.$globalConstants.host.menu_items.forEach((item) => {
                if (!item.dropdown_items || item.dropdown_items.length === 0) {
                    noDropdownItems.main_links.push(item);
                } else {
                    hasDropdownItems.push(item);
                }
            });
            if (this.isAdsenseExternalProject && this.dropdown_items) {
                return this.dropdown_items.filter(
                    (item) => item.route !== '/report-content'
                );
            }

            if (noDropdownItems.main_links.length === 0) {
                return [this.defaultLinks, ...hasDropdownItems];
            }

            /*return [this.defaultLinks, ...hasDropdownItems, noDropdownItems]; */
            return [
                this.filteredDefaultLinks,
                ...hasDropdownItems,
                noDropdownItems,
            ];
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/mixins';

.gw-footer {
    border-top: 1px solid var(--border-color);
    background-color: white;
    text-align: center;
    font-size: 16px;
    color: var(--primary);
    width: 100%;
    content-visibility: auto;
    contain-intrinsic-size: 935px;

    @include mq('tablet-wide') {
        text-align: unset;
        contain-intrinsic-size: 550px;
    }

    .container {
        margin: auto;
        max-width: 1180px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: center;
        padding: 20px 0;
        @include mq('tablet-wide') {
            flex-direction: row;
            padding: 40px;
            flex-wrap: wrap;
            justify-content: flex-start;
            gap: 20px 0;
            align-items: stretch;
        }

        section {
            padding: 20px;
            flex: 0 1 25%;
            width: 100%;
            flex-direction: column;
            align-items: center;

            .menu-item {
                display: flex;
                flex-direction: column;
                list-style-type: none;
                width: 100%;
                gap: 20px;
                @include mq('tablet-wide') {
                }

                .menu-item__link {
                    font-size: 18px;
                    text-transform: none;
                    font-weight: bold;
                    color: var(--primary);
                    text-decoration: none;
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .menu-item__list {
                    font-size: 16px;
                    line-height: 1.5;
                    color: var(--primary);
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    transition: 0.3 s all ease;

                    li {
                        &:hover {
                            color: rgba(var(--primary), 0.8);
                        }
                    }
                }
            }
        }

        section:not(:last-of-type) {
            border-bottom: 1px solid var(--border-color);
            @include mq('tablet-wide') {
                border-bottom: none;
            }
        }

        section:last-of-type {
            @include mq('tablet-wide') {
                border-right: none !important;
            }
        }

        section:not(:nth-child(4n)) {
            border-right: 1px solid var(--border-color);
        }

        .footer-logo {
            display: flex;
            justify-content: center;
            margin-bottom: 40px;
            min-height: 50px;

            img {
                object-fit: contain;
                display: block;
                aspect-ratio: 180 / 50;
            }
        }

        .social-media-links {
            display: flex;
            justify-content: center;
            min-height: 28px;
        }
    }

    .bottom {
        background: var(--tertiary-background);
        text-align: center;
        padding: 10px 0;
        font-size: 15px;
        line-height: 22px;
        color: var(--secondary);
    }

    ul {
        list-style-type: none;

        li {
            text-decoration: none;
        }
    }
    a {
        color: inherit;
    }
    .footer-links-skeleton {
        width: 100%;
        min-height: 234px; /* adjust to match typical links height */
    }
}
</style>
